import { useMemo } from 'react';
import Avatar from '../Avatar';
import { useNudge } from '../../lib/moments';
import { peopleListSurfaceClass } from '../../config/glassSurfaces';
import {
  FIND_PEER_FALLBACK_LABEL,
  isUuidLikeString,
} from '../../lib/proximity/findPeerDisplayName';
import { findRowDistance, findRowAge } from '../../lib/proximity/findRowText';

/**
 * Everyone in your groups for the event, under the radar - on the Find tab and on Find inside Maps.
 *
 * Row 1 from Sam's 2026-10-13 review: the Followers / Following container, the name larger and centred on the line,
 * the distance over how old it is on the right. The age wears the map's colour (positionAgeTone); an old position is
 * never faded.
 *
 * `emphasizedPeerId`: the person picked from the Find picker comes to the top with a ring. Emphasis only — never a filter.
 */
export default function FindPeerList({
  peers,
  trackedPeerId,
  onPeerTap,
  className = '',
  emphasizedPeerId = null,
}) {
  // The first-time path (lib/moments): the first person glows until you track someone.
  const trackGlow = useNudge('track', !trackedPeerId);
  const rows = useMemo(() => {
    const mapped = (peers ?? []).map((p) => ({
      peerId: p.peerId,
      displayName: isUuidLikeString(p.displayName)
        ? FIND_PEER_FALLBACK_LABEL
        : p.displayName || FIND_PEER_FALLBACK_LABEL,
      photoUrl: p.photoUrl ?? null,
      distance: findRowDistance(p),
      age: findRowAge(p),
      kind: p.display?.kind ?? null,
      isTracked: p.peerId === trackedPeerId,
      isEmphasized: p.peerId === emphasizedPeerId,
    }));
    if (!emphasizedPeerId) return mapped;
    return [...mapped.filter((r) => r.isEmphasized), ...mapped.filter((r) => !r.isEmphasized)];
  }, [peers, trackedPeerId, emphasizedPeerId]);

  if (rows.length === 0) return null;

  return (
    <ul className={`mb-6 ${peopleListSurfaceClass()} ${className}`}>
      {rows.map((row, i) => (
        <li key={row.peerId}>
          {/* data-peer-id / data-kind: which person by which signal, for the sim phones (scripts/sim) */}
          <button
            type="button"
            data-peer-id={row.peerId}
            data-kind={row.kind ?? undefined}
            onClick={() => onPeerTap?.(row.peerId)}
            className={`${i === 0 ? trackGlow : ''} flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors ${
              row.isTracked
                ? 'bg-primary/10'
                : row.isEmphasized
                  ? 'ring-1 ring-primary/30'
                  : 'hover:bg-white/5'
            }`}
          >
            <Avatar displayName={row.displayName} profilePhotoUrl={row.photoUrl} size="sm" />
            <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-dark-text glass-surface-text-emphasis">
              {row.displayName}
            </span>
            {(row.distance || row.age) && (
              <span className="flex shrink-0 flex-col items-end leading-tight">
                {row.distance && (
                  <span className="text-sm font-semibold tabular-nums text-dark-text">
                    {row.distance}
                  </span>
                )}
                {row.age && <span className={`text-xs seen--${row.age.tone}`}>{row.age.text}</span>}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
